import { useEffect, useMemo, useRef } from "react";

const canonicalize = (value) => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== "object") return value;

  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = canonicalize(value[key]);
      return result;
    }, {});
};

export const getEditableProjectSignature = (project) =>
  JSON.stringify(
    canonicalize({
      credits: project?.credits ?? null,
      fontStyle: project?.fontStyle ?? null,
      googleSheetLink: project?.googleSheetLink ?? null,
      googleSpreadSheetId: project?.googleSpreadSheetId ?? null,
      logos: project?.logos ?? [],
      resolution: project?.resolution ?? null,
      settings: project?.settings ?? null,
    }),
  );

const useResetRenderedStatusOnEdit = ({ enabled, project, onReset }) => {
  const shouldTrack =
    enabled && Boolean(project?._id) && project?.projectStatus === "Rendered";
  const signature = useMemo(
    () => (shouldTrack ? getEditableProjectSignature(project) : null),
    [
      shouldTrack,
      project?.credits,
      project?.fontStyle,
      project?.googleSheetLink,
      project?.googleSpreadSheetId,
      project?.logos,
      project?.resolution,
      project?.settings,
    ],
  );
  const renderedBaseline = useRef({ projectId: "", signature: null });

  useEffect(() => {
    const currentProjectId = String(project?._id || "");

    if (!enabled || !currentProjectId) {
      renderedBaseline.current = { projectId: "", signature: null };
      return;
    }

    if (!shouldTrack) {
      renderedBaseline.current = {
        projectId: currentProjectId,
        signature: null,
      };
      return;
    }

    if (
      renderedBaseline.current.projectId !== currentProjectId ||
      renderedBaseline.current.signature === null
    ) {
      renderedBaseline.current = {
        projectId: currentProjectId,
        signature,
      };
      return;
    }

    if (renderedBaseline.current.signature === signature) return;

    renderedBaseline.current = {
      projectId: currentProjectId,
      signature,
    };
    onReset();
  }, [enabled, onReset, project?._id, shouldTrack, signature]);
};

export default useResetRenderedStatusOnEdit;
